import type {
  ArrayExpression,
  BooleanExpression,
  DynamicValue,
  Expression,
  NumberExpression,
  StringExpression,
} from '@wirechunk/schemas/expressions/expression.d.ts';
import { isEqual, isNil, isNumber, isPlainObject } from 'lodash-es';
import { arrayOrDefaultNull } from '../arrays.ts';
import { booleanOrDefaultNull } from '../booleans.ts';
import { exhaustive } from '../exhaustive.ts';
import { numberOrDefaultNull } from '../numbers.ts';
import { stringOrDefaultNull } from '../strings.ts';
import {
  isArrayOperator,
  isBooleanOperator,
  isNumberOperator,
  isStringOperator,
} from './type-util.ts';

// Special keys that are set on data objects only on the client.
export const currentTimeKey = Symbol.for('current-time');
export const eventTypeKey = Symbol.for('event-type');
export const propsKey = Symbol.for('props');
export const userProductItemsKey = Symbol.for('user-product-items');
export const userRoleKey = Symbol.for('user-role');

type SpecialKey =
  | typeof currentTimeKey
  | typeof eventTypeKey
  | typeof propsKey
  | typeof userProductItemsKey
  | typeof userRoleKey;

// Returns the specified property in obj if it is a plain object and has a defined value for the key.
// Otherwise, returns null. Will not return undefined.
const getPropertySafe = (obj: unknown, key: string | SpecialKey): unknown => {
  if (isPlainObject(obj)) {
    return (obj as Record<string | SpecialKey, unknown>)[key] ?? null;
  }
  return null;
};

const evaluateDynamicValue = (expr: DynamicValue, data: unknown) => {
  switch (expr.operator) {
    case 'dataValue':
      return expr.name ? getPropertySafe(data, expr.name) : null;
    case 'prop':
      return expr.name ? getPropertySafe(getPropertySafe(data, propsKey), expr.name) : null;
    default:
      return exhaustive(expr);
  }
};

export const evaluateArrayExpression = (expr: ArrayExpression, data: unknown): unknown[] | null => {
  switch (expr.operator) {
    case 'dataValue':
    case 'prop':
      return arrayOrDefaultNull(evaluateDynamicValue(expr, data));
    case 'arrayLiteral':
      return expr.value.map((element) => evaluateExpression(element, data));
    case 'filter': {
      const { arrayExpression, filterExpression } = expr;
      return (
        evaluateArrayExpression(arrayExpression, data)?.filter(
          (element) => evaluateBooleanExpression(filterExpression, element) === true,
        ) ?? null
      );
    }
    case 'map': {
      const { arrayExpression, mapExpression } = expr;
      return (
        evaluateArrayExpression(arrayExpression, data)?.map((element) =>
          evaluateExpression(mapExpression, element),
        ) ?? null
      );
    }
    case 'userProductItems':
      return arrayOrDefaultNull(getPropertySafe(data, userProductItemsKey));
    default:
      return arrayOrDefaultNull(evaluateDynamicValue(expr, data));
  }
};

export const evaluateBooleanExpression = (
  expr: BooleanExpression,
  data: unknown,
): boolean | null => {
  switch (expr.operator) {
    case 'dataValue':
    case 'prop':
      return booleanOrDefaultNull(evaluateDynamicValue(expr, data));
    case 'all': {
      return (
        evaluateArrayExpression(expr.arrayExpression, data)?.every(
          // We check for equality with true so that nulls and other non-booleans are treated as false.
          (element) => evaluateBooleanExpression(expr.condition, element) === true,
        ) ?? null
      );
    }
    case 'arrayIncludes': {
      const { arrayExpression, value } = expr;
      const evaluatedValue = evaluateExpression(value, data);
      if (isNil(evaluatedValue)) {
        // Similar to the equals operator, we return null for any comparison to a null value.
        return null;
      }
      return (
        evaluateArrayExpression(arrayExpression, data)?.some((element) =>
          isEqual(element, evaluatedValue),
        ) ?? null
      );
    }
    case 'greaterThan': {
      const evaluatedLeft = evaluateNumberExpression(expr.left, data);
      const evaluatedRight = evaluateNumberExpression(expr.right, data);
      if (isNil(evaluatedLeft) || isNil(evaluatedRight)) {
        return null;
      }
      return evaluatedLeft > evaluatedRight;
    }
    case 'booleanLiteral':
      return expr.value;
    case 'both': {
      const evaluatedLeft = evaluateBooleanExpression(expr.left, data);
      if (evaluatedLeft === false) {
        return false;
      }
      const evaluatedRight = evaluateBooleanExpression(expr.right, data);
      if (evaluatedRight === false) {
        return false;
      }
      if (isNil(evaluatedLeft) || isNil(evaluatedRight)) {
        return null;
      }
      return true;
    }
    case 'either': {
      const evaluatedLeft = evaluateBooleanExpression(expr.left, data);
      if (evaluatedLeft === true) {
        return true;
      }
      const evaluatedRight = evaluateBooleanExpression(expr.right, data);
      if (evaluatedRight === true) {
        return true;
      }
      if (isNil(evaluatedLeft) || isNil(evaluatedRight)) {
        return null;
      }
      return false;
    }
    case 'equals': {
      const evaluatedLeft = evaluateExpression(expr.left, data);
      const evaluatedRight = evaluateExpression(expr.right, data);
      if (isNil(evaluatedLeft) || isNil(evaluatedRight)) {
        return null;
      }
      return isEqual(evaluatedLeft, evaluatedRight);
    }
    case 'exists':
      return !isNil(evaluateExpression(expr.expression, data));
    case 'not': {
      const evaluatedExpression = evaluateBooleanExpression(expr.expression, data);
      if (isNil(evaluatedExpression)) {
        return null;
      }
      return !evaluatedExpression;
    }
    case 'some': {
      const { arrayExpression, condition } = expr;
      return (
        evaluateArrayExpression(arrayExpression, data)?.some(
          (element) => evaluateBooleanExpression(condition, element) === true,
        ) ?? null
      );
    }
    default:
      return booleanOrDefaultNull(evaluateDynamicValue(expr, data));
  }
};

export const evaluateNumberExpression = (expr: NumberExpression, data: unknown): number | null => {
  switch (expr.operator) {
    case 'dataValue':
    case 'prop':
      return numberOrDefaultNull(evaluateDynamicValue(expr, data));
    case 'currentTime':
      return numberOrDefaultNull(getPropertySafe(data, currentTimeKey));
    case 'length': {
      const evaluatedValue = evaluateExpression(expr.value, data);
      if (Array.isArray(evaluatedValue)) {
        return evaluatedValue.length;
      }
      if (typeof evaluatedValue === 'string') {
        return evaluatedValue.length;
      }
      return null;
    }
    case 'numberLiteral':
      return expr.value;
    case 'sum': {
      const evaluatedLeft = evaluateNumberExpression(expr.left, data);
      const evaluatedRight = evaluateNumberExpression(expr.right, data);
      if (isNil(evaluatedLeft) || isNil(evaluatedRight)) {
        return null;
      }
      return evaluatedLeft + evaluatedRight;
    }
    case 'sumArray': {
      return (
        evaluateArrayExpression(expr.arrayExpression, data)?.reduce<number>(
          (sum, element) => sum + (isNumber(element) ? element : 0),
          0,
        ) ?? null
      );
    }
    case 'timeLiteral':
      return expr.value;
    default:
      return numberOrDefaultNull(evaluateDynamicValue(expr, data));
  }
};

export const evaluateStringExpression = (expr: StringExpression, data: unknown): string | null => {
  switch (expr.operator) {
    case 'dataValue':
    case 'prop':
      return stringOrDefaultNull(evaluateDynamicValue(expr, data));
    case 'eventType':
      return stringOrDefaultNull(getPropertySafe(data, eventTypeKey));
    case 'stringLiteral':
      return expr.value;
    case 'userRole':
      return stringOrDefaultNull(getPropertySafe(data, userRoleKey));
    default:
      return stringOrDefaultNull(evaluateDynamicValue(expr, data));
  }
};

const isArrayExpression = (expr: Expression): expr is ArrayExpression =>
  isArrayOperator(expr.operator);

export const isBooleanExpression = (expr: Expression): expr is BooleanExpression =>
  isBooleanOperator(expr.operator);

const isNumberExpression = (expr: Expression): expr is NumberExpression =>
  isNumberOperator(expr.operator);

const isStringExpression = (expr: Expression): expr is StringExpression =>
  isStringOperator(expr.operator);

const evaluateExpression = (expr: Expression, data: unknown) => {
  switch (expr.operator) {
    case 'dataValue':
    case 'prop':
      return evaluateDynamicValue(expr, data);
    default:
      if (isArrayExpression(expr)) {
        return evaluateArrayExpression(expr, data);
      }
      if (isBooleanExpression(expr)) {
        return evaluateBooleanExpression(expr, data);
      }
      if (isNumberExpression(expr)) {
        return evaluateNumberExpression(expr, data);
      }
      if (isStringExpression(expr)) {
        return evaluateStringExpression(expr, data);
      }
      return exhaustive(expr);
  }
};
